import {
	Divider,
	Link as ChakraLink,
	useMultiStyleConfig,
	chakra,
	ChakraProps,
} from "@chakra-ui/react";
import React from "react";
import { NextLink } from "src/components/NextLink";
import { useTranslatedString } from "src/i18n/hooks";
import { ArrowRight, ChevronUp } from "src/icons";
import { getDataFromRecord } from "src/lib/cms/utils";
import { hexColorWithOpacity, truthy } from "src/lib/utils";
import { HeaderDropdownFragment } from "../../__generated__/cms-schema.codegen";
import { addDividerToHeaderMenuItems } from "../Header/CMSMenuItems";
import {
	HeaderAccordion,
	HeaderAccordionButton as ChakraHeaderAccordionButton,
	HeaderAccordionContent,
} from "../HeaderAccordion/HeaderAccordion";
import {
	HeaderDropdown,
	HeaderDropdownButton,
	HeaderDropdownContent,
	HeaderDropdownItem,
	MenuArrow,
} from "../HeaderDropdown/HeaderDropdown";
import { getLinkProps } from "../Link/utils";

type HeaderSubMenuProps = {
	label: string;
	items: HeaderDropdownFragment["dropdownItems"];
	id: string;
};

const HeaderAccordionButton: React.FC<{ name: string } & ChakraProps> = ({
	name,
	...rest
}) => {
	const styles = useMultiStyleConfig("HeaderDropdown");

	return (
		<ChakraHeaderAccordionButton
			sx={styles.accordionButton}
			style={{
				background: "none",
				color: "white",
				textDecoration: "none",
			}}
			{...rest}
		>
			{name}
		</ChakraHeaderAccordionButton>
	);
};

const SecondaryLevelHeaderItem: React.FC<{
	items: HeaderDropdownFragment["dropdownItems"][number]["secondLevelItems"];
	isMobile?: boolean;
}> = ({ items, isMobile }) => {
	const styles = useMultiStyleConfig("HeaderDropdown");
	const t = useTranslatedString();

	if (isMobile) {
		return items
			.map(({ id, label, link, thirdLevelItems }) => {
				const { name, href } = getDataFromRecord(link);

				if (thirdLevelItems.length > 0) {
					return (
						<HeaderAccordion key={id}>
							<HeaderAccordionButton
								name={label?.length ? label : name}
							/>
							<HeaderAccordionContent
								sx={{
									"hr + hr": {
										display: "none",
									},
								}}
							>
								<Divider />
								<NextLink passHref href={href}>
									<ChakraLink
										id={id}
										textDecoration="none"
										fontWeight={600}
										paddingLeft={24}
									>
										{t("headerSubMenu.overview")}{" "}
										{label?.length ? label : link.name}
									</ChakraLink>
								</NextLink>
								<Divider />
								{thirdLevelItems
									.filter(truthy)
									.map(({ id, label, link }) => {
										const { href } =
											getDataFromRecord(link);

										return (
											<NextLink
												key={id}
												passHref
												href={href}
											>
												<ChakraLink
													textDecoration="none"
													fontWeight={500}
													paddingLeft={24}
													id={id}
												>
													{label?.length
														? label
														: link.name}
												</ChakraLink>
											</NextLink>
										);
									})
									.flatMap(
										addDividerToHeaderMenuItems(
											<Divider />,
										),
									)}
							</HeaderAccordionContent>
						</HeaderAccordion>
					);
				}

				const externalLink =
					link.__typename === "ProductPageRecord" && link.externalLink
						? getLinkProps(link.externalLink)
						: null;

				return (
					<chakra.div key={id}>
						{externalLink?.href ? (
							<ChakraLink
								id={id}
								textDecoration="none"
								fontWeight={600}
								paddingLeft={8}
								href={externalLink.href}
								isExternal
							>
								{label?.length ? label : link.name}
							</ChakraLink>
						) : (
							<NextLink passHref href={href}>
								<ChakraLink
									id={id}
									textDecoration="none"
									fontWeight={600}
									paddingLeft={8}
								>
									{label?.length ? label : link.name}
								</ChakraLink>
							</NextLink>
						)}
					</chakra.div>
				);
			})
			.flatMap(addDividerToHeaderMenuItems(<Divider />));
	}

	return items.map(({ id, label, link, thirdLevelItems }) => {
		const { name, href } = getDataFromRecord(link);

		return (
			<chakra.div pt="4" key={id}>
				<HeaderDropdownItem
					href={href}
					label={label?.length ? label : name}
					icon={<ArrowRight sx={styles.arrow} />}
					fontWeight="bold"
					fontSize="md"
				/>
				{thirdLevelItems.map(({ id, label, link }) => {
					const { name, href } = getDataFromRecord(link);

					return (
						<HeaderDropdownItem
							key={id}
							href={href}
							label={label?.length ? label : name}
							icon={<ArrowRight sx={styles.arrow} />}
							fontSize="sm"
							fontWeight={500}
						/>
					);
				})}
			</chakra.div>
		);
	});
};

const HeaderDropdownItems: React.FC<{
	items: HeaderDropdownFragment["dropdownItems"];
	isMobile?: boolean;
}> = ({ items, isMobile }) => {
	const styles = useMultiStyleConfig("HeaderDropdown");
	const t = useTranslatedString();

	if (isMobile) {
		return items
			.map(({ id, label, link, secondLevelItems }) => {
				const { name, href } = getDataFromRecord(link);

				return (
					<HeaderAccordion key={id}>
						<HeaderAccordionButton
							textTransform="uppercase"
							name={label?.length ? label : name}
						/>
						<HeaderAccordionContent>
							<Divider />
							<NextLink passHref href={href}>
								<ChakraLink
									textDecoration="none"
									fontWeight={600}
									paddingLeft={8}
									id={id}
								>{`${t("headerSubMenu.overview")} ${
									label?.length
										? label
										: link.name
											? label
											: link.name
								}`}</ChakraLink>
							</NextLink>
							<Divider />
							<SecondaryLevelHeaderItem
								items={secondLevelItems}
								isMobile
							/>
						</HeaderAccordionContent>
					</HeaderAccordion>
				);
			})
			.flatMap(addDividerToHeaderMenuItems(<Divider />));
	}

	return (
		<chakra.div display="flex" flexDir="column" flexWrap="wrap">
			<chakra.div display="flex">
				{items.map(({ id, label, link, secondLevelItems }) => {
					const { name, href } = getDataFromRecord(link);

					return (
						<chakra.div key={id} padding="4">
							<HeaderDropdownItem
								href={href}
								label={label?.length ? label : name}
								icon={<ArrowRight sx={styles.arrow} />}
								sx={styles.family}
								backgroundColor={
									link.__typename ===
									"InsuranceProductFamilyRecord"
										? `${hexColorWithOpacity(link.color.hex, 0.15)}`
										: undefined
								}
							/>
							<SecondaryLevelHeaderItem
								items={secondLevelItems}
							/>
						</chakra.div>
					);
				})}
			</chakra.div>
		</chakra.div>
	);
};

export const HeaderSubMenu: React.FC<HeaderSubMenuProps> = ({
	id,
	label,
	items,
}) => {
	return (
		<>
			<HeaderAccordion
				display={{
					base: "block",
					desktopNavigation: "none",
				}}
			>
				<ChakraHeaderAccordionButton id={id}>
					{label}
				</ChakraHeaderAccordionButton>
				<HeaderAccordionContent
					sx={{
						"hr + hr": {
							display: "none",
						},
					}}
				>
					<HeaderDropdownItems items={items} isMobile />
				</HeaderAccordionContent>
			</HeaderAccordion>

			<HeaderDropdown>
				<HeaderDropdownButton
					display={{
						base: "none",
						desktopNavigation: "block",
					}}
					id={id}
					variant="headerDropdown"
				>
					{label}
					<ChevronUp />
				</HeaderDropdownButton>

				<HeaderDropdownContent
					display={{
						base: "none",
						desktopNavigation: "block",
					}}
				>
					<MenuArrow />
					<HeaderDropdownItems items={items} />
				</HeaderDropdownContent>
			</HeaderDropdown>
		</>
	);
};

// 🔬 TBD: Please evaluate
